import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { 
  getServices, 
  adminCreateService, 
  adminUpdateService, 
  adminDeleteService 
} from '../api/api';

export const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'Package',
    image: '',
    order: 0,
    page_intro: '',
    page_content: '',
    bullet_points: []
  });

  // Doit correspondre exactement à iconMap dans components/Services.jsx
  const iconOptions = [
    'Building2', 'Home', 'HardHat', 'Sparkles', 'Building', 'Briefcase',
    'Package', 'KeyRound', 'Trash2', 'Truck', 'Wind', 'Sofa',
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        title: service.title,
        description: service.description,
        icon: service.icon,
        image: service.image,
        order: service.order,
        page_intro: service.page_intro || '',
        page_content: service.page_content || '',
        bullet_points: service.bullet_points || []
      });
    } else {
      setEditingService(null);
      setFormData({
        title: '',
        description: '',
        icon: 'Package',
        image: '',
        order: services.length + 1,
        page_intro: '',
        page_content: '',
        bullet_points: []
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingService(null);
    setFormData({
      title: '',
      description: '',
      icon: 'Package',
      image: '',
      order: 0,
      page_intro: '',
      page_content: '',
      bullet_points: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingService) {
        await adminUpdateService(editingService.id, formData);
        toast.success('Service mis à jour avec succès');
      } else {
        await adminCreateService(formData);
        toast.success('Service créé avec succès');
      }
      
      handleCloseDialog();
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
      return;
    }

    try {
      await adminDeleteService(id);
      toast.success('Service supprimé avec succès');
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des services</h2>
          <p className="text-gray-600">Gérez les services affichés sur votre site</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau service
        </Button>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <Card key={service.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <div className="relative h-48 overflow-hidden rounded-t-xl">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2 flex space-x-2">
                <button
                  onClick={() => handleOpenDialog(service)}
                  className="p-2 bg-white rounded-lg hover:bg-blue-50 transition-colors shadow-lg"
                >
                  <Edit className="w-4 h-4 text-blue-600" />
                </button>
                <button
                  onClick={() => handleDelete(service.id)}
                  className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors shadow-lg"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-lg text-gray-900">{service.title}</h3>
                <span className="text-sm text-gray-500">#{service.order}</span>
              </div>
              <p className="text-gray-600 text-sm">{service.description}</p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-500">Icône: {service.icon}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Modifier le service' : 'Nouveau service'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Ordre</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="icon">Icône</Label>
              <select
                id="icon"
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                {iconOptions.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image">URL de l'image *</Label>
              <Input
                id="image"
                type="url"
                value={formData.image}
                onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                placeholder="https://images.unsplash.com/..."
                required
              />
              {formData.image && (
                <img
                  src={formData.image}
                  alt="Aperçu"
                  loading="lazy"
                  className="w-full h-32 object-cover rounded-lg mt-2"
                />
              )}
            </div>

            {/* Page dédiée : c'est ce contenu qui permet au service d'être
                trouvé sur Google indépendamment de la page d'accueil. */}
            <div className="border-t border-gray-200 pt-5 mt-2 space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900">Page dédiée du service</h4>
                <p className="text-sm text-gray-500 mt-1">
                  {editingService?.slug ? (
                    <>
                      Accessible sur{' '}
                      <a
                        href={`/services/${editingService.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        /services/{editingService.slug}
                      </a>
                    </>
                  ) : (
                    "L'adresse de la page sera créée automatiquement à partir du titre."
                  )}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="page_intro">Phrase d'accroche</Label>
                <Textarea
                  id="page_intro"
                  value={formData.page_intro}
                  onChange={(e) => setFormData(prev => ({ ...prev, page_intro: e.target.value }))}
                  rows={2}
                  placeholder="Nettoyage complet de vos bureaux, adapté à vos horaires."
                />
                <p className="text-xs text-gray-500">
                  Affichée en haut de la page et reprise par Google sous le titre du résultat.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bullets">Points clés</Label>
                <Textarea
                  id="bullets"
                  value={formData.bullet_points.join('\n')}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    bullet_points: e.target.value.split('\n').filter(Boolean)
                  }))}
                  rows={4}
                  placeholder={'Intervention en dehors des heures d\'ouverture\nProduits écologiques\nPersonnel assuré'}
                />
                <p className="text-xs text-gray-500">Un point par ligne.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="page_content">Contenu détaillé</Label>
                <Textarea
                  id="page_content"
                  value={formData.page_content}
                  onChange={(e) => setFormData(prev => ({ ...prev, page_content: e.target.value }))}
                  rows={10}
                  className="font-mono text-sm"
                  placeholder={'## Ce que comprend la prestation\n\nDécrivez le déroulement, le matériel, la fréquence…'}
                />
                <p className="text-xs text-gray-500">
                  Mise en forme : <code>## Titre</code>, <code>**gras**</code>,
                  <code className="ml-1">- élément de liste</code>.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingService ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
